import React, { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Users, Calendar, Settings, LogOut, Menu, X, MessageSquare, Mail, Truck,
  PanelLeftClose, PanelLeftOpen, Wallet,
} from 'lucide-react';
import { useAuth } from '../../shared/contexts/contexts/AuthContext';
import { toast } from 'sonner';

const COLLAPSE_KEY = 'admin:sidebar-collapsed';

const AdminLayout: React.FC = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Telas densas (check-in, pedidos, concursos) ganham espaço com o menu
  // recolhido, e a escolha acompanha o operador entre as páginas.
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });

  const toggleCollapsed = () => {
    setCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* sem storage */ }
      return next;
    });
  };
  const { logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogout = async () => {
    try {
      await logout();
      toast.success('Logout realizado com sucesso!', {
        description: 'Até logo!'
      });
      // Forçar navegação e limpeza de estado
      navigate('/admin/login', { replace: true });
      // Recarregar a página para limpar qualquer estado cacheado
      window.location.reload();
    } catch (error) {
      console.error('Erro ao fazer logout:', error);
      // fallback: Mesmo em caso de erro, limpar e redirecionar
      navigate('/admin/login', { replace: true });
    }
  };

  const menuItems = [
    {
      name: 'Dashboard',
      path: '/admin/dashboard',
      icon: Menu,
    },
    {
      name: 'Clientes',
      path: '/admin/clients',
      icon: Users,
    },
    {
      name: 'Eventos',
      path: '/admin/events',
      icon: Calendar,
    },
    {
      name: 'Repasses',
      path: '/admin/repasses',
      icon: Wallet,
    },
    {
      name: 'Fornecedores',
      path: '/admin/fornecedores',
      icon: Truck,
    },
    {
      name: 'Solicitações',
      path: '/admin/solicitations',
      icon: Mail,
    },
    {
      name: 'Depoimentos',
      path: '/admin/testimonials',
      icon: MessageSquare,
    },
    {
      name: 'Configurações',
      path: '/admin/settings',
      icon: Settings,
    },
  ];

  const isActivePath = (path: string) => {
    return location.pathname === path;
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 sm:w-72 bg-white shadow-lg transform ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${collapsed ? 'lg:w-20' : 'lg:w-72'}`}>
        <div className={`flex items-center h-14 sm:h-16 border-b border-gray-200 ${collapsed ? 'lg:justify-center lg:px-2' : 'justify-between'} px-4 sm:px-6`}>
          <div className={`flex items-center min-w-0 gap-2 ${collapsed ? 'lg:gap-0' : ''}`}>
            <img
              src="/images/logo_simbolo_sombra.png"
              alt="Better Now"
              className="h-8 w-8 object-contain shrink-0"
            />
            <div className={`flex items-baseline min-w-0 ${collapsed ? 'lg:hidden' : ''}`}>
              <span className="text-base sm:text-lg font-bold text-gray-900 truncate">Better Now</span>
              <span className="ml-1.5 text-[10px] font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-md uppercase shrink-0">Admin</span>
            </div>
          </div>
          <button
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-1.5 sm:p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 flex-shrink-0"
          >
            <X className="h-5 w-5 sm:h-6 sm:w-6" />
          </button>
          <button
            onClick={toggleCollapsed}
            className={`hidden lg:block p-2 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 flex-shrink-0 ${collapsed ? 'lg:hidden' : ''}`}
            title="Recolher menu"
            aria-label="Recolher menu"
          >
            <PanelLeftClose className="h-5 w-5" />
          </button>
        </div>

        {collapsed && (
          <button
            onClick={toggleCollapsed}
            className="hidden lg:flex w-full items-center justify-center py-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50"
            title="Expandir menu"
            aria-label="Expandir menu"
          >
            <PanelLeftOpen className="h-5 w-5" />
          </button>
        )}

        <nav className={`mt-4 sm:mt-8 px-3 sm:px-4 ${collapsed ? 'lg:px-2 lg:mt-3' : ''}`}>
          <ul className="space-y-1 sm:space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              return (
                <li key={item.path}>
                  <button
                    onClick={() => {
                      navigate(item.path);
                      setSidebarOpen(false);
                    }}
                    title={collapsed ? item.name : undefined}
                    className={`w-full flex items-center px-3 sm:px-4 py-2.5 sm:py-3 text-sm font-medium rounded-lg transition-colors ${collapsed ? 'lg:justify-center lg:px-0' : ''} ${isActivePath(item.path)
                        ? 'bg-blue-50 text-blue-700 border-r-2 border-blue-700'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                      }`}
                  >
                    <Icon className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 mr-2 sm:mr-3 ${collapsed ? 'lg:mr-0' : ''}`} />
                    <span className={`truncate ${collapsed ? 'lg:hidden' : ''}`}>{item.name}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Logout Button */}
        <div className="absolute bottom-0 left-0 right-0 p-3 sm:p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            title={collapsed ? 'Sair' : undefined}
            className={`w-full flex items-center px-3 sm:px-4 py-2.5 sm:py-3 text-sm font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors ${collapsed ? 'lg:justify-center lg:px-0' : ''}`}
          >
            <LogOut className={`h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0 mr-2 sm:mr-3 ${collapsed ? 'lg:mr-0' : ''}`} />
            <span className={`truncate ${collapsed ? 'lg:hidden' : ''}`}>Sair</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 min-w-0 lg:ml-0">
        {/* Mobile Header */}
        <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 sticky top-0 z-30">
          <div className="flex items-center justify-between h-14 sm:h-16 px-3 sm:px-4">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-1.5 sm:p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 flex-shrink-0"
            >
              <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
            </button>
            <div className="flex items-center min-w-0 gap-2">
              <img
                src="/images/logo_simbolo_sombra.png"
                alt="Better Now"
                className="h-7 w-7 object-contain shrink-0"
              />
              <div className="flex items-baseline min-w-0">
                <span className="text-sm sm:text-base font-bold text-gray-900 truncate">Better Now</span>
                <span className="ml-1 text-[10px] font-bold text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded-md uppercase shrink-0">Admin</span>
              </div>
            </div>
            <div className="w-8 sm:w-10 flex-shrink-0"></div> {/* Spacer */}
          </div>
        </div>

        {/* Page Content */}
        <main className="flex-1 p-3 sm:p-4 md:p-6">
          <Outlet />
        </main>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;